import {
  Clipboard,
  HStack,
  Icon,
  IconButton,
  Link,
  Text,
  type TextProps,
} from '@chakra-ui/react'
import { Check, Copy, ExternalLink } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * A deliberately narrow passthrough, not the full `TextProps` surface.
 *
 * `fontSize` and `fontWeight` are excluded on purpose: the component owns its
 * type scale so every copyable value reads the same, and callers set the
 * context (a table cell, a `DataList.ItemValue`) rather than restyling each
 * instance.
 * What is left is layout — the value renders as a `<p>` or an `<a>` depending
 * on `externalLink`, and DOM event handlers typed for one element don't fit
 * the other.
 */
type PassthroughStyleProps = Pick<
  TextProps,
  'color' | 'fontFamily' | 'maxW' | 'minW' | 'width'
>

type Props = {
  /**
   * The value written to the clipboard, and the displayed text unless
   * `children` overrides it.
   */
  value?: string | null
  /**
   * Renders the value as a link to this URL, opened in a new tab, instead of
   * plain text.
   */
  externalLink?: string
  /**
   * Renders the value as a link that runs this handler — for in-app
   * navigation, where there is no URL to hand to `externalLink`. Ignored when
   * `externalLink` is set.
   */
  onClick?: () => void
  /** Overrides the displayed text. Defaults to `value`. */
  children?: ReactNode
  /** Shown when there is no value. Defaults to the shared "N/A" string. */
  emptyText?: string
  /** Hide the copy affordance for values nobody copies. */
  hasCopy?: boolean
} & PassthroughStyleProps

/**
 * A value you can read and copy: plain text (or a link) with a copy button
 * beside it.
 *
 * The alternative, `DataTag`, wraps the value in a coloured pill. That reads
 * as a status chip, which is wrong for ordinary field values — an org name is
 * not a badge. Use this for read-and-copy fields, and keep `DataTag` for
 * places where the pill framing is the point.
 *
 * Copy state comes from `Clipboard.Indicator`, so there is no timer to leak
 * on unmount.
 */
export function CopyableValue({
  value,
  externalLink,
  onClick,
  children,
  emptyText,
  hasCopy = true,
  ...text
}: Readonly<Props>) {
  const { t } = useTranslation(undefined, { useSuspense: false })
  const isEmpty = value === undefined || value === null || value === ''

  if (isEmpty) {
    return (
      <Text color="fg.muted" fontSize="sm" {...text}>
        {emptyText ?? String(t('general.notAvailable'))}
      </Text>
    )
  }

  const displayed = children ?? value

  function renderValue() {
    if (externalLink) {
      return (
        <Link
          href={externalLink}
          target="_blank"
          rel="noopener noreferrer"
          colorPalette="blue"
          truncate
          {...text}
        >
          {displayed}
          <Icon as={ExternalLink} boxSize={3.5} />
        </Link>
      )
    }
    if (onClick) {
      // A real <button>, not a click handler on text: it has to be reachable
      // by keyboard, and there is no URL to make it an anchor.
      return (
        <Link as="button" type="button" onClick={onClick} truncate {...text}>
          {displayed}
        </Link>
      )
    }
    return (
      <Text truncate {...text}>
        {displayed}
      </Text>
    )
  }

  return (
    <HStack gap={1} minW={0} maxW="100%">
      {renderValue()}

      {hasCopy && (
        // `aria-label` is set explicitly: Clipboard.Trigger otherwise injects
        // its own generic label, which loses which value is being copied.
        <Clipboard.Root value={value}>
          <Clipboard.Trigger asChild>
            <IconButton
              variant="ghost"
              size="2xs"
              aria-label={String(t('general.copyToClipboard'))}
            >
              <Clipboard.Indicator
                copied={<Icon as={Check} color="icon.icon-success" />}
              >
                <Icon as={Copy} />
              </Clipboard.Indicator>
            </IconButton>
          </Clipboard.Trigger>
        </Clipboard.Root>
      )}
    </HStack>
  )
}
